import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';
import {
  GetRecurringPayments,
  SetCurrentRecurringPayment,
} from '@app/finxone-web-frontend/app/lib/actions/recurring-payments.action';
import { ConfigService } from '@app/finxone-web-frontend/app/lib/services/config-service/config-service.service';
import { CountriesCurrenciesService } from '@app/finxone-web-frontend/app/lib/services/countries-currencies-service/countries-currencies.service';
import { NavigationService } from '@app/finxone-web-frontend/app/lib/services/navigation-service/navigation.service';
import {
  RecurringPaymentsState,
  RecurringPaymentsStateModel,
} from '@app/finxone-web-frontend/app/lib/state/recurring-payments.state';
import { SavingsAccountState } from '@app/finxone-web-frontend/app/lib/state/savings-account.state';
import { scrollLoading } from '@app/finxone-web-frontend/app/lib/utils/scrolling-utils/scrolling.utils';
import {
  GenericHandleBarModel,
  buildHandleBarModel,
  filterIfHandlebarNotRendered,
} from '@app/finxone-web-frontend/app/lib/utils/template/model-builder.utils';
import { createRedirectionUrlFromNavigationOptions } from '@app/finxone-web-frontend/app/lib/utils/utils';
import { AutoDestroy, UUID } from '@finxone-platform/shared/base-types';
import { IconCacheService } from '@finxone-platform/shared/services';
import {
  ActionConfig,
  BaseCssConfig,
  BaseCssProperties,
  BaseWidgetProperties,
  ListActionValue,
  ListingAction,
  NavigationOptionEnum,
  NavigationOptionsType,
  ProviderClass,
  RecurringPaymentRow,
  RecurringPaymentType,
  SKELETON_LAYOUT_CONFIG_PATH,
  UiZoneWidgetAttributeConfig,
  ViewType,
  ZoneActionOptions,
  baseCssConfigStyle,
  isRecurringPaymentCancellable,
  resolveSkeletonLoadingTemplate,
} from '@finxone-platform/shared/sys-config-types';
import { Select, Store } from '@ngxs/store';
import Handlebars from 'handlebars';
import { Observable, Subject, takeUntil } from 'rxjs';

/** App builder preview states */
const APP_BUILDER_FETCHING_STATE = 'fetching';
const APP_BUILDER_NO_RECORDS_STATE = 'noRecords';

/**
 * The list item template is builder-authored HTML, so a list row only opts into the configured
 * navigation when the builder tags an element inside it with this id. The table view renders its
 * own column templates instead, so there the whole row is the trigger.
 */
const VIEW_DETAIL_ELEMENT_ID = 'view-detail';

@Component({
  standalone: false,
  selector: 'finxone-web-frontend-recurring-payment-list-widget',
  templateUrl: './recurring-payment-list-widget.component.html',
})
export class RecurringPaymentListWidgetComponent implements OnInit, AfterViewInit {
  @AutoDestroy private readonly destroy$: Subject<void> = new Subject<void>();

  @Select(RecurringPaymentsState.getRecurringPayments)
  recurringPayments$!: Observable<RecurringPaymentsStateModel>;

  public recurringPayments: RecurringPaymentRow[] = [];
  public isApiInProgress = false;
  public viewType = ViewType.LIST;
  public viewTypes = ViewType;
  public widgetProperties: BaseWidgetProperties;
  public baseCssConfig: BaseCssConfig;
  public contentStyle: baseCssConfigStyle;
  public textContentStyle: baseCssConfigStyle;
  public isGlobalStylingEnabled = false;
  public containerHeight: string;
  public appBuilderShowFetchingContent = false;
  public appBuilderShowNoRecordsContent = false;
  public renderedNoRecordsTemplate = '';
  public renderedFetchingTemplate = '';
  public listContainerId = 'recurring-payment-list-' + new UUID().toString();
  public pageNumber = 1;
  public totalListCount = 0;
  private baseHandleBarModel: GenericHandleBarModel;
  public readonly menuPopupCss = {
    'margin-top': '5px',
    'border-radius': '6px',
  };
  public actionOptions: ZoneActionOptions[] = [];
  public menuItemsStatus: {
    [rowId: string]: { [actionValue: string]: boolean };
  } = {};

  public widgetConfig: ProviderClass<UiZoneWidgetAttributeConfig> = inject(ProviderClass);

  private readonly elementRef = inject(ElementRef);
  private readonly store = inject(Store);
  private readonly configService = inject(ConfigService);
  private readonly countriesCurrenciesService = inject(CountriesCurrenciesService);
  private readonly iconCacheService = inject(IconCacheService);
  private readonly navigationService = inject(NavigationService);
  private readonly router = inject(Router);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  ngOnInit(): void {
    this.initStyles();
    this.initWidgetProperties();
    this.loadTemplates();
    this.listenToRecurringPayments();
    if (!this.isAppBuilderPreviewState) {
      this.fetchRecurringPayments();
    }
  }

  ngAfterViewInit(): void {
    this.containerHeight = scrollLoading(this.listContainerId, this.elementRef);
    this.changeDetectorRef.detectChanges();
  }

  private initStyles(): void {
    this.baseCssConfig = this.widgetConfig.attri?.css_config;
    // a linked list takes each row's box from the theme's List styling; the widget's own box styles the container
    this.isGlobalStylingEnabled =
      !!this.widgetConfig?.attri?.widgetProperties?.['globalStyling'] &&
      (this.widgetConfig?.attri?.['viewType'] ?? ViewType.LIST) === ViewType.LIST;
    this.contentStyle = this.configService.getSpecificBaseCssStyle(this.widgetConfig?.attri, [
      BaseCssProperties.MARGIN,
      BaseCssProperties.PADDING,
      BaseCssProperties.COLORS_V2,
      BaseCssProperties.BORDER_V2,
      BaseCssProperties.BOX_SHADOW,
    ]);
    this.textContentStyle = this.configService.getSpecificBaseCssStyle(this.widgetConfig?.attri, []);
  }

  private initWidgetProperties(): void {
    this.widgetProperties = this.widgetConfig?.attri?.widgetProperties ?? {};
    this.viewType = this.widgetConfig?.attri?.['viewType'] ?? ViewType.LIST;
    this.actionOptions = this.widgetConfig?.attri?.widgetProperties?.['menuOptions'];

    if (this.configService.getActiveConfigType().isWorkflow) {
      const appBuilderState = this.widgetProperties?.['appBuilderState'];
      this.appBuilderShowFetchingContent = appBuilderState === APP_BUILDER_FETCHING_STATE;
      this.appBuilderShowNoRecordsContent = appBuilderState === APP_BUILDER_NO_RECORDS_STATE;
    }
  }

  private loadTemplates(): void {
    const noRecordsTemplate = Handlebars.compile(this.widgetConfig?.attri?.['noRecordsTemplate'] ?? '');
    const fetchingTemplate = Handlebars.compile(this.widgetConfig?.attri?.['fetchingTemplate'] ?? '');

    buildHandleBarModel(this.store, '', this.countriesCurrenciesService, this.iconCacheService)
      .pipe(takeUntil(this.destroy$))
      .subscribe((model) => {
        this.baseHandleBarModel = model;
        this.renderedNoRecordsTemplate = filterIfHandlebarNotRendered(noRecordsTemplate(model));
        this.renderedFetchingTemplate = resolveSkeletonLoadingTemplate(
          this.widgetConfig?.attri?.[SKELETON_LAYOUT_CONFIG_PATH],
          filterIfHandlebarNotRendered(fetchingTemplate(model)),
          this.viewType,
        );
      });
  }

  private listenToRecurringPayments(): void {
    this.recurringPayments$.pipe(takeUntil(this.destroy$)).subscribe((state) => {
      this.recurringPayments = this.withRowActions(state?.recurringPaymentList ?? []);
      this.totalListCount = this.recurringPayments?.length ?? 0;
      this.isApiInProgress = state?.isLoading ?? false;
    });
  }

  private withRowActions(recurringPayments: RecurringPaymentType[]): RecurringPaymentRow[] {
    const options = this.actionOptions ?? [];
    const menuItemsStatus: { [rowId: string]: { [actionValue: string]: boolean } } = {};

    const rows = recurringPayments.map((recurringPayment) => {
      let visibleOptionCount = 0;

      options.forEach((option) => {
        if (this.isMenuOptionVisible(option, recurringPayment)) {
          visibleOptionCount++;
          return;
        }
        menuItemsStatus[recurringPayment.id] = {
          ...menuItemsStatus[recurringPayment.id],
          [option.value]: false,
        };
      });

      return {
        ...recurringPayment,
        actionEnabled: visibleOptionCount > 0,
        cancelEnabled: isRecurringPaymentCancellable(recurringPayment.status),
      };
    });

    this.menuItemsStatus = menuItemsStatus;
    return rows;
  }

  /**
   * `isIdNotExist` is finx-table's own per-row rule for a menu option; it is mirrored here so the
   * count that decides the ellipsis matches what the menu would actually render.
   */
  private isMenuOptionVisible(option: ZoneActionOptions, recurringPayment: RecurringPaymentType): boolean {
    if (option.isIdNotExist && (recurringPayment as Record<string, unknown>)[option.isIdNotExist]) {
      return false;
    }
    if (option.value === ListActionValue.CANCEL) {
      return isRecurringPaymentCancellable(recurringPayment.status);
    }
    return true;
  }

  private fetchRecurringPayments(): void {
    this.store.dispatch(new GetRecurringPayments({ accountId: this.accountIdForContext }));
  }

  private get accountIdForContext(): string {
    return this.store.selectSnapshot(SavingsAccountState.getCurrentAccountId) || '';
  }

  public get pageSize(): number {
    return Number(this.widgetConfig?.attri?.['pageSize'] ?? 10);
  }

  public get pagingEnabled(): boolean {
    return this.widgetConfig?.attri?.['pagingEnabled'] ?? true;
  }

  private get isAppBuilderPreviewState(): boolean {
    return this.appBuilderShowFetchingContent || this.appBuilderShowNoRecordsContent;
  }

  /** The builder's "Fetching" preview stands in for a real in-flight first page. */
  public get showFetchingState(): boolean {
    return this.appBuilderShowFetchingContent || (this.isApiInProgress && !this.recurringPayments.length);
  }

  public get displayData(): RecurringPaymentRow[] {
    if (this.isAppBuilderPreviewState) return [];
    if (this.viewType !== ViewType.TABLE || !this.pagingEnabled) return this.recurringPayments;

    const startIndex = (this.pageNumber - 1) * this.pageSize;
    return this.recurringPayments.slice(startIndex, startIndex + this.pageSize);
  }

  public recurringPaymentHandleBarModel(recurringPayment: RecurringPaymentRow, index: number) {
    return {
      ...this.baseHandleBarModel,
      ...recurringPayment,
      recurringPayment,
      index,
    };
  }

  /** Actions the builder revealed behind the row for the configured gestures. */
  public get listActions(): ListingAction {
    return this.widgetConfig?.attri?.['listActions'] || {};
  }

  /** A table row is only made clickable once the builder has pointed it at a page. */
  public get enableClickableRow(): boolean {
    return this.viewType === ViewType.TABLE && this.hasNavigationConfigured;
  }

  private get hasNavigationConfigured(): boolean {
    const navigationOption = this.widgetConfig?.attri?.['navigationOption'];
    return !!navigationOption && navigationOption !== NavigationOptionEnum.USE_AS_BLANK;
  }

  public onListItemClick(event: Event, recurringPayment: RecurringPaymentType): void {
    const target = event.target as HTMLElement | null;
    if (!target?.closest(`[id="${VIEW_DETAIL_ELEMENT_ID}"]`)) return;
    this.openRecurringPaymentDetail(recurringPayment);
  }

  /** finx-table emits the row itself, so there is no element to look for a view-detail id on. */
  public onTableItemClick(recurringPayment: RecurringPaymentType): void {
    this.openRecurringPaymentDetail(recurringPayment);
  }

  /** Only stash the clicked mandate when there is a detail page configured to consume it. */
  private openRecurringPaymentDetail(recurringPayment: RecurringPaymentType): void {
    if (!this.hasNavigationConfigured) return;
    this.store.dispatch(new SetCurrentRecurringPayment(recurringPayment));
    this.navigateToConfiguredPage();
  }

  private navigateToConfiguredPage(): void {
    if (this.configService.getActiveConfigType().isWorkflow) return;

    const { pageUrl } = this.navigationService.buildPageNavigationUrl({
      pageId: this.widgetConfig?.attri?.['pageId'] || '',
      zone: this.widgetConfig?.attri?.['zone'] || '',
      pageUrl: this.widgetConfig?.attri?.['pageUrl'] || '',
      role: this.widgetConfig?.attri?.['role'] || '',
      navigationOption: this.widgetConfig?.attri?.['navigationOption'],
      tabRef: this.widgetConfig?.attri?.['tabRef'] ?? '',
      pageTransition: this.widgetConfig?.attri?.['pageTransition'] ?? 'forward',
      browserBehaviour: this.widgetConfig?.attri?.['browserBehaviour'],
      externalUrl: this.widgetConfig?.attri?.['externalUrl'],
    });

    if (pageUrl) {
      this.router.navigateByUrl(pageUrl);
    }
  }

  public onPageChange(event: { first: number; page: number; pageCount: number; rows: number }): void {
    this.pageNumber = event.first / event.rows + 1;
    this.widgetConfig.attri['pageSize'] = event.rows;
  }

  public performAction(
    event: { actionKey: string; actionId: string; actionOption?: ZoneActionOptions },
    navigationOptionConfig?: NavigationOptionsType,
  ) {
    const recurringPaymentState = this.store.selectSnapshot(RecurringPaymentsState.getRecurringPayments);
    const recurringPayment = recurringPaymentState.recurringPaymentList.find(
      (recurringPayment) => recurringPayment.id === event.actionId,
    );

    if (event.actionKey === ListActionValue.CANCEL) {
      if (!isRecurringPaymentCancellable(recurringPayment?.status)) return;
      this.store.dispatch(new SetCurrentRecurringPayment(recurringPayment as RecurringPaymentType));
    }

    let redirectUrl = this.configService.reRouteIfModal(
      event.actionOption?.zoneToNavigate || '',
      event.actionOption?.urlToNavigate || '',
    );

    if (navigationOptionConfig) {
      redirectUrl = createRedirectionUrlFromNavigationOptions(navigationOptionConfig);
    }
    this.router.navigateByUrl(redirectUrl);
  }

  public onListActionTriggered(actionEvent: any, recurringPayment: RecurringPaymentType): void {
    const navigationOptionConfig = actionEvent?.config?.navigationOptionConfig;
    if (
      !navigationOptionConfig ||
      navigationOptionConfig.navigationOption === NavigationOptionEnum.USE_AS_BLANK
    )
      return;

    this.performAction(
      { actionKey: actionEvent.action, actionId: recurringPayment?.id ?? '' },
      navigationOptionConfig,
    );
  }

  /** finx-table hands back the action config and the whole row, not an action key/id pair. */
  public handleAction(event: { actionConfig: ActionConfig; data: RecurringPaymentType }): void {
    const navigationOptionConfig = event.actionConfig?.navigationOptionConfig;
    if (!event.data?.id || navigationOptionConfig?.navigationOption === NavigationOptionEnum.USE_AS_BLANK) {
      return;
    }

    this.performAction(
      { actionKey: event.actionConfig.action, actionId: event.data.id },
      navigationOptionConfig,
    );
  }
}
